import { sendAnalytics } from '@audio-network/analytics'

import { AutocompletePlugin } from '@/components/pages/search/SearchBox/SearchBox.types'
import { shouldOfferPromptAiInTypeahead } from '@/stores/searchSelectorStore'
import useSearchSelectorStore from '@/stores/searchSelectorStore'
import type { AutocompleteFetcher } from '@/utils/algolia/autocomplete'
import { shouldFetchAutocompleteForQuery } from '@/utils/algolia/autocomplete/utils'
import type { QueryHit } from '@/utils/algolia/autocompleteHitTypes'

import { isValidPromptSearchTerm } from '../../components'

// Query suggestions + AI prompt in one plugin (Algolia plugins cannot share state).
export const suggestionsAndPromptPlugin = (
	user: User | null,
	refine: (query: string) => void,
	clearAiSearch: () => void,
	setSearchByPrompt: (prompt: string) => void,
	fetchAutocomplete: AutocompleteFetcher
): AutocompletePlugin<QueryHit> => {
	return {
		name: 'suggestionsAndPromptPlugin',
		getSources() {
			return [
				{
					sourceId: 'suggestionsAndPromptSource',
					getItemInputValue({ item }) {
						return item.query || ''
					},
					getItems({ query }) {
						if (!shouldFetchAutocompleteForQuery(query)) {
							return []
						}

						return fetchAutocomplete(query).then((batch) => {
							const items: QueryHit[] = []

							const promptAiAllowed = shouldOfferPromptAiInTypeahead(
								useSearchSelectorStore.getState().searchType
							)

							if (user && promptAiAllowed && isValidPromptSearchTerm(query)) {
								items.push({ objectID: 'promptAI', query })
							}

							items.push(...batch.suggestionHits)

							return items
						})
					},
					onSelect({ item }) {
						if (item.objectID === 'promptAI') {
							const treatAsPromptSearch = shouldOfferPromptAiInTypeahead(
								useSearchSelectorStore.getState().searchType
							)
							if (!treatAsPromptSearch) {
								refine(item.query)
								clearAiSearch()
								return
							}

							setSearchByPrompt(item.query)

							sendAnalytics({
								noun: 'Prompt Search',
								pastTenseVerb: 'Submitted',
							})
						} else {
							refine(item.query)
							clearAiSearch()

							sendAnalytics({
								noun: 'Typeahead',
								pastTenseVerb: 'Submitted',
								eventParameters: {
									Term: item.query,
									Type: 'Query Suggestion',
								},
							})
						}
					},
				},
			]
		},
	}
}
